import React, { useState, useEffect } from 'react';
import {
   Modal,
   Box,
   Typography,
   TextField,
   Button,
   IconButton,
   Avatar,
   Chip,
   Tooltip,
   Collapse
} from '@mui/material';
import { FluxIcon } from '@meltwater/flux-web-components-react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteIcon from '@mui/icons-material/Delete';
import styles from './admin.module.css';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { formatBirthdayDate } from '../../utils/dateUtils';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { getInitialsFromEmail, getDisplayNameFromEmail } from '../../utils/userUtils';
import { formatlastLogin } from '../admin/admin.functions';
import PermissionsSection from './PermissionsSection';
import GroupsSection from './GroupsSection';
import AdminService from '../../services/admin.services';

/**
 * Edit User Modal Component
 * Modal for editing user groups and viewing user profile information
 */
const EditUserModal = ({
   open = false,
   onClose,
   profileData = {},
   groups = [],
   GSDgroups = [],
   onGroupsChange,
   permission = [],
   onPermissionChange,
   sendgridPermission = [],
   onSendgridPermissionChange,
   mediaRelationsPermission = [],
   onMediaRelationsPermissionChange,
   companiesPermission = [],
   onCompaniesPermissionChange,
   dashboardPermission = [],
   onDashboardPermissionChange,
   twitterXPermission = [],
   onTwitterXPermissionChange,
   entitlementPermission = [],
   onEntitlementPermissionChange,
   intercomPermission = [],
   onIntercomPermissionChange,
   toolsPermission = [],
   onToolsPermissionChange,
   auth0Permission = [],
   onAuth0PermissionChange,
   scriptRunnerv2Permission = [],
   onScriptRunnerv2PermissionChange,
   active = false,
   onToggleEditMode,
   onUpdateProfile,
   onBlockUser,
   onDeleteUser,
   onDeleteField,
   onResetBirthdayTimer,
   isLoading = false
}) => {
   const [showJson, setShowJson] = useState(false);
   const [showPermissions, setShowPermissions] = useState(false);
   const [showUsageTracking, setShowUsageTracking] = useState(false);
   const [usageTrackingData, setUsageTrackingData] = useState(null);
   const [loadingUsageTracking, setLoadingUsageTracking] = useState(false);

   // Reset usage tracking data when modal closes or user changes
   useEffect(() => {
      if (!open) {
         setShowUsageTracking(false);
         setUsageTrackingData(null);
         setLoadingUsageTracking(false);
      }
   }, [open, profileData.email]);

   // Check if permissions have changed from the original profile data
   const hasPermissionChanges = (
      JSON.stringify(permission) !== JSON.stringify(profileData.permissions || []) ||
      JSON.stringify(sendgridPermission) !== JSON.stringify(profileData.sendgridpermissions || []) ||
      JSON.stringify(mediaRelationsPermission) !== JSON.stringify(profileData.mediarelationspermissions || []) ||
      JSON.stringify(companiesPermission) !== JSON.stringify(profileData.companiespermissions || []) ||
      JSON.stringify(dashboardPermission) !== JSON.stringify(profileData.dashboardpermissions || []) ||
      JSON.stringify(twitterXPermission) !== JSON.stringify(profileData.twitterxpermissions || []) ||
      JSON.stringify(entitlementPermission) !== JSON.stringify(profileData.entitlementpermissions || []) ||
      JSON.stringify(intercomPermission) !== JSON.stringify(profileData.intercompermissions || []) ||
      JSON.stringify(toolsPermission) !== JSON.stringify(profileData.toolspermissions || []) ||
      JSON.stringify(auth0Permission) !== JSON.stringify(profileData.auth0permissions || []) ||
      JSON.stringify(scriptRunnerv2Permission) !== JSON.stringify(profileData.scriptRunnerv2permissions || [])
   );

   // Sanitize profile data by removing sensitive or unnecessary fields
   const sanitizedProfile = React.useMemo(() => {
      const {
         profilePictureKey,
         profilePictureUrl,
         birthdayUpdatedAt,
         ...sanitized
      } = profileData;
      return sanitized;
   }, [profileData]);

   const renderProfileField = (label, value, fieldKey, showDeleteButton = true) => {
      const hasValue = value && value.toString().trim() !== '';

      return (
         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
               <strong>{label}:</strong> {hasValue ? value : 'Not set'}
            </Typography>
            {showDeleteButton && hasValue && (
               <Tooltip title={`Delete ${label.toLowerCase()}`}>
                  <IconButton
                     size="small"
                     color="error"
                     onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDeleteField(profileData.email, fieldKey, label);
                     }}
                     sx={{ padding: '2px' }}
                  >
                     <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
               </Tooltip>
            )}
         </Box>
      );
   };

   const renderBirthdayField = () => {
      if (profileData.birthday) {
         return renderProfileField(
            'Birthday',
            formatBirthdayDate(profileData.birthday),
            'birthday'
         );
      } else {
         return (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
               <Typography variant="body2" color="text.secondary">
                  <strong>Birthday:</strong> Not set
                  {profileData.birthdayUpdatedAt ? "" : " (birthday timer not in place)"}
               </Typography>
               {profileData.birthdayUpdatedAt && (
                  <Tooltip title="Reset birthday restriction timer">
                     <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                           e.preventDefault();
                           e.stopPropagation();
                           onResetBirthdayTimer(profileData.email);
                        }}
                        sx={{ padding: '2px' }}
                     >
                        <RestartAltIcon fontSize="small" />
                     </IconButton>
                  </Tooltip>
               )}
            </Box>
         );
      };
   };

   return (
      <Modal
         open={open}
         onClose={onClose}
         aria-labelledby="edit-user-modal-title"
         aria-describedby="edit-user-modal-description"
      >
         <Box className={styles.modalContainer}>
            <div className={styles.modalHeader}>
               <Typography variant="h6" component="h2" className={styles.modalTitle}>
                  User: {getDisplayNameFromEmail(profileData.email)}
               </Typography>
               <IconButton onClick={onClose} size="small" className={styles.closeButton}>
                  <FluxIcon name="actions:close" />
               </IconButton>
            </div>

            <div className={styles.modalContent}>
               {/* User Profile Picture Section */}
               <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar
                     src={profileData.profilePictureUrl || profileData.profilePicture || ''}
                     alt={profileData.email?.split('@')[0]}
                     sx={{ width: 64, height: 64, bgcolor: !(profileData.profilePictureUrl || profileData.profilePicture) ? '#1D9F9F' : 'inherit', mr: 2 }}
                  >
                     {!(profileData.profilePictureUrl || profileData.profilePicture) && getInitialsFromEmail(profileData.email || '')}
                  </Avatar>
                  <Box sx={{ flexGrow: 1, minWidth: '300px' }}>
                     <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {profileData.preferredName || getDisplayNameFromEmail(profileData.email)}
                     </Typography>
                     <TextField
                        label="Email"
                        fullWidth
                        value={profileData.email || ''}
                        disabled
                        variant="outlined"
                        size="small"
                        InputProps={{
                           readOnly: true,
                        }}
                        sx={{ mt: 1, minWidth: '300px', width: '100%' }}
                     />
                  </Box>
               </Box>

               {/* Status indicator */}
               <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mr: 1 }}>Status:</Typography>
                  {profileData.isBlocked ? (
                     <Chip
                        icon={<BlockIcon fontSize="small" />}
                        label="Blocked"
                        color="error"
                        size="small"
                        variant="outlined"
                     />
                  ) : (
                     <Chip
                        icon={<CheckCircleIcon fontSize="small" />}
                        label="Active"
                        color="success"
                        size="small"
                        variant="outlined"
                     />
                  )}
               </Box>

               {/* User Profile Data Section - Read Only */}
               <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" className={styles.sectionTitle}>
                     Profile Information
                  </Typography>
                  <Box sx={{ p: 1, backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                     {renderProfileField(
                        'Profile Picture',
                        (profileData.profilePicture || profileData.profilePictureKey || profileData.profilePictureUrl) ? 'Available' : null,
                        'profilePicture'
                     )}
                     {renderProfileField('Preferred Name', profileData.preferredName, 'preferredName')}
                     {renderProfileField('Location', profileData.city, 'city')}
                     {renderProfileField('Timezone', profileData.timezone, 'timezone')}
                     {renderProfileField('Status Message', profileData.statusMessage, 'statusMessage')}
                     {renderBirthdayField()}
                     <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                           <strong>Last Login:</strong> {profileData.lastLogin ? formatlastLogin(profileData.lastLogin) : 'Not available'}
                        </Typography>
                     </Box>
                  </Box>
               </Box>

               {/* Groups section - Only editable section */}
               <GroupsSection
                  groups={groups}
                  GSDgroups={GSDgroups}
                  onGroupsChange={onGroupsChange}
                  disabled={!active}
                  opacity={0.7}
               />

               {/* Permissions Section */}
               <Box sx={{ mt: 2 }}>
                  <Button
                     onClick={() => setShowPermissions(!showPermissions)}
                     startIcon={showPermissions ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                     sx={{
                        color: 'text.secondary',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: 'transparent' }
                     }}
                  >
                     Individual Permissions (Override Groups)
                  </Button>
                  <Collapse in={showPermissions}>
                     <Box sx={{ mt: 1, p: 2, backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                        <PermissionsSection
                           permission={permission}
                           onPermissionChange={onPermissionChange}
                           dashboardPermission={dashboardPermission}
                           onDashboardPermissionChange={onDashboardPermissionChange}
                           sendgridPermission={sendgridPermission}
                           onSendgridPermissionChange={onSendgridPermissionChange}
                           mediaRelationsPermission={mediaRelationsPermission}
                           onMediaRelationsPermissionChange={onMediaRelationsPermissionChange}
                           companiesPermission={companiesPermission}
                           onCompaniesPermissionChange={onCompaniesPermissionChange}
                           twitterXPermission={twitterXPermission}
                           onTwitterXPermissionChange={onTwitterXPermissionChange}
                           entitlementPermission={entitlementPermission}
                           onEntitlementPermissionChange={onEntitlementPermissionChange}
                           intercomPermission={intercomPermission}
                           onIntercomPermissionChange={onIntercomPermissionChange}
                           toolsPermission={toolsPermission}
                           onToolsPermissionChange={onToolsPermissionChange}
                           auth0Permission={auth0Permission}
                           onAuth0PermissionChange={onAuth0PermissionChange}
                           scriptRunnerv2Permission={scriptRunnerv2Permission}
                           onScriptRunnerv2PermissionChange={onScriptRunnerv2PermissionChange}
                           disabled={!active}
                           opacity={0.7}
                        />
                     </Box>
                  </Collapse>
               </Box>

               {/* Usage Tracking Section */}
               <Box sx={{ mt: 2 }}>
                  <Button
                     onClick={async () => {
                        const newShowState = !showUsageTracking;
                        setShowUsageTracking(newShowState);
                        if (newShowState && !usageTrackingData && profileData.email) {
                           setLoadingUsageTracking(true);
                           try {
                              const response = await AdminService.getUserUsageTracking(profileData.email);
                              setUsageTrackingData(response.data);
                           } catch (error) {
                              console.error('Error fetching usage tracking:', error);
                              // Handle 404 - user has no usage tracking data yet
                              if (error.response && error.response.status === 404) {
                                 setUsageTrackingData({ message: 'No usage tracking data found for this user' });
                              } else {
                                 setUsageTrackingData({ error: error.message || 'Failed to load usage tracking data' });
                              }
                           } finally {
                              setLoadingUsageTracking(false);
                           }
                        }
                     }}
                     startIcon={showUsageTracking ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                     sx={{
                        color: 'text.secondary',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: 'transparent' }
                     }}
                  >
                     View Usage Tracking (Last Active)
                  </Button>
                  <Collapse in={showUsageTracking}>
                     <Box
                        sx={{
                           mt: 1,
                           p: 2,
                           backgroundColor: '#f5f5f5',
                           borderRadius: '4px',
                           maxHeight: '500px',
                           overflow: 'auto',
                           border: '1px solid #e0e0e0'
                        }}
                     >
                        {loadingUsageTracking ? (
                           <Typography variant="body2">Loading usage tracking data...</Typography>
                        ) : usageTrackingData ? (
                           usageTrackingData.error ? (
                              <Typography variant="body2" color="error">
                                 Error: {usageTrackingData.error}
                              </Typography>
                           ) : (
                              <pre style={{
                                 margin: 0,
                                 fontSize: '0.75rem',
                                 fontFamily: 'monospace',
                                 whiteSpace: 'pre-wrap',
                                 wordBreak: 'break-all',
                                 overflowWrap: 'break-word',
                                 lineHeight: '1.5'
                              }}>
                                 {JSON.stringify(usageTrackingData, null, 2)}
                              </pre>
                           )
                        ) : (
                           <Typography variant="body2">Click to load usage tracking data</Typography>
                        )}
                     </Box>
                  </Collapse>
               </Box>

               {/* JSON Preview Section */}
               <Box sx={{ mt: 2 }}>
                  <Button
                     onClick={() => setShowJson(!showJson)}
                     startIcon={showJson ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                     sx={{
                        color: 'text.secondary',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: 'transparent' }
                     }}
                  >
                     View Profile JSON
                  </Button>
                  <Collapse in={showJson}>
                     <Box
                        sx={{
                           mt: 1,
                           p: 2,
                           backgroundColor: '#f5f5f5',
                           borderRadius: '4px',
                           maxHeight: '500px',
                           overflow: 'auto',
                           border: '1px solid #e0e0e0'
                        }}
                     >
                        <pre style={{
                           margin: 0,
                           fontSize: '0.75rem',
                           fontFamily: 'monospace',
                           whiteSpace: 'pre-wrap',
                           wordBreak: 'break-all',
                           overflowWrap: 'break-word',
                           lineHeight: '1.5'
                        }}>
                           {JSON.stringify(sanitizedProfile, null, 2)}
                        </pre>
                     </Box>
                  </Collapse>
               </Box>
            </div>

            <div className={styles.modalFooter}>
               {!active ? (
                  <Button
                     variant="contained"
                     color="primary"
                     onClick={onToggleEditMode}
                     startIcon={<EditIcon />}
                     className={styles.primaryButton}
                  >
                     Edit Groups & Permissions
                  </Button>
               ) : (
                  <Button
                     variant="contained"
                     color="primary"
                     onClick={onUpdateProfile}
                     className={styles.primaryButton}
                     disabled={
                        JSON.stringify(groups) === JSON.stringify(profileData.groups || []) &&
                        !hasPermissionChanges
                     }
                  >
                     {hasPermissionChanges && JSON.stringify(groups) !== JSON.stringify(profileData.groups || [])
                        ? 'Save Groups & Permissions'
                        : hasPermissionChanges
                           ? 'Save Permissions'
                           : 'Save Groups'
                     }
                  </Button>
               )}

               <Button
                  variant="contained"
                  color={profileData.isBlocked ? "success" : "warning"}
                  onClick={() => onBlockUser(profileData.email, !profileData.isBlocked)}
                  className={profileData.isBlocked ? styles.successButton : styles.warningButton}
                  startIcon={profileData.isBlocked ? <CheckCircleIcon /> : <BlockIcon />}
               >
                  {profileData.isBlocked ? "Unblock" : "Block"}
               </Button>

               <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                     onClose();
                     onDeleteUser(profileData.email);
                  }}
                  className={styles.dangerButton}
                  startIcon={<DeleteIcon />}
               >
                  Delete
               </Button>
            </div>
         </Box>
      </Modal>
   );
};

export default EditUserModal;
